import OneSchemaImporter from "@oneschema/react";
import type { SolutionCSSColorPalette } from "@smartsuite/react-ui";
import {
  Button,
  Modal,
  ModalHeader,
  ModalOverlay,
  Preloader,
} from "@smartsuite/react-ui";
import { changeLanguage, t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import type { ImportUIProps } from "../..";
import { ChooseApplicationDropdown } from "../../components/ChooseApplicationDropdown";
import { InfoIconButton } from "../../components/InfoIconButton/InfoIconButton";
import { UpdateExistingRecordsButton } from "../../components/UpdateExistingRecordsButton";
import type { UpdateSettings } from "../../components/UpdateExistingRecordsButton/types";
import { getSolutionCSSColorPalette } from "../../services/colorHelper";
import { intercomOpenImportArticle } from "../../services/intercom.service";
import { OneSchema } from "../../services/oneSchema";
import { ImportType } from "../../stores/config.store";
import { useRootStore } from "../../stores/hooks";
import * as S from "./style";

export const App = observer<ImportUIProps>((props) => {
  const { config, theme, applications } = useRootStore();
  const mainColor = props.solution?.logo_color ?? props.logoColor;

  const colorPalette: SolutionCSSColorPalette =
    getSolutionCSSColorPalette(mainColor);
  theme.setThemeColor({
    primaryColor: mainColor,
    hoverColor: colorPalette["solution-hover-color"],
    secondaryColor: colorPalette["solution-secondary-color"],
    highlightColor: colorPalette["solution-highlight-color"],
  });

  void changeLanguage(config.userPreferences.language.value);
  const [templateKey, setTemplateKey] = useState("");
  const [uploadUrl, setUploadUrl] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const oneSchema = new OneSchema();

  useEffect(() => {
    if (!jwtToken || !templateKey) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [templateKey, jwtToken]);

  const getAccountId = (): string =>
    props.accountId ?? localStorage.getItem("accountId") ?? "";
  const getRole = (): string =>
    props.role ?? localStorage.getItem("role") ?? "";
  const appId = applications.selectedApplication?.id ?? props.applicationId;

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const response = await oneSchema.getApplications(
        config.solutionId,
        getAccountId(),
      );
      applications.setList(response);
      if (!applications.selectedApplication) {
        applications.setSelectedApplication(
          applications.list.find((app) => app.id === props.applicationId),
        );
      }
    };

    if (!props.applications && config.importType === ImportType.solution) {
      void fetchData();
    }
  }, [config.solutionId, config.importType]);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(false);

    const fetchData = async (applicationId: string): Promise<void> => {
      try {
        const token = await buildJwtToken(appId as string);
        const accessToken =
          props.accessToken ?? localStorage.getItem("accessToken") ?? undefined;

        const response = await oneSchema.createTemplate({
          accountId: getAccountId(),
          jwtToken: token,
          applicationId,
          accessToken,
          application: applications.selectedApplication,
        });

        if (!response.templateKey) {
          throw new Error("create-template response is missing templateKey");
        }

        setTemplateKey(response.templateKey);
        setUploadUrl(response.uploadUrl);
      } catch (error) {
        console.error("Failed to initialize the import template", error);
        setLoadError(true);
        setIsLoading(false);
      }
    };

    if (appId) {
      void fetchData(String(appId));
    }
  }, [appId, reloadToken]);

  const handleRetryLoad = (): void => {
    setLoadError(false);
    setTemplateKey("");
    setIsLoading(true);
    setReloadToken((token) => token + 1);
  };

  useEffect(() => {
    if (appId) {
      props.onSelectApplication?.(appId);
    }
  }, [appId]);

  const buildJwtToken = async (applicationId: string): Promise<string> => {
    const isSolutionOwner = config.solution.permissions.owners.includes(
      config.member.id,
    );

    const jwt = await oneSchema.makeJwt({
      accountId: getAccountId(),
      applicationId,
      userPreferences: config.userPreferences,
      role: getRole(),
      memberId: config.member.id,
      recordsLimit: getRecordsLimit(),
      isSolutionOwner,
      featureFlags: config.featureFlags,
    });
    setJwtToken(jwt);

    return jwt;
  };

  const getSolutionRecordsLimit = (): number | undefined => {
    const solution = config.solution;
    const plan = config.account.plan;
    if (plan.allow_records_overage || plan.records_per_solution === null) {
      return undefined;
    }
    let recordsLimit =
      plan.records_per_solution - (solution.records_count ?? 0);
    if (recordsLimit <= 0) {
      recordsLimit = 0;
    }
    return recordsLimit;
  };

  // here take min from solution and imort limit.
  // import limit is taken from monolith and it is dependent on account plan
  const getRecordsLimit = (): number => {
    const solutionRecordsLimit = getSolutionRecordsLimit();
    const importRecordsLimit = config.recordsLimit;
    if (solutionRecordsLimit === undefined) {
      return importRecordsLimit;
    }
    return Math.min(solutionRecordsLimit, importRecordsLimit);
  };

  const [isOpen, setIsOpen] = useState(true);
  const [updateSettings, setUpdateSettings] = useState<
    UpdateSettings | undefined
  >(undefined);

  const onClose = (): void => {
    props.onClose?.();
  };

  useEffect(() => {
    if (updateSettings !== undefined && templateKey !== undefined) {
      void oneSchema.saveUpdateSettings(
        getAccountId(),
        templateKey,
        updateSettings,
      );
    }
  }, [templateKey, updateSettings]);

  return (
    <ModalOverlay
      animation="none"
      active={true}
      closeByEsc={true}
      onClose={onClose}
    >
      <S.LocalCssVariablesWrapper>
        <Modal
          size="XL"
          styles={{
            width: "calc(100vw - 60px)",
            height: "calc(100vh - 120px)",
            top: "-30px",
          }}
          header={
            <>
              <ModalHeader
                title={t("import.import")}
                actions={
                  <InfoIconButton
                    iconName="question-circle"
                    onClick={intercomOpenImportArticle}
                  />
                }
              />
              {config.importType === ImportType.solution && (
                <S.ApplicationButtonLayout>
                  <ChooseApplicationDropdown
                    onOpenCreateAppModal={props.onOpenCreateAppModal}
                  />
                </S.ApplicationButtonLayout>
              )}
              <S.UpdateExistingRecordsButtonLayout>
                <UpdateExistingRecordsButton
                  updateSettings={updateSettings}
                  onChange={(updateSettings) => {
                    setUpdateSettings(updateSettings);
                  }}
                />
              </S.UpdateExistingRecordsButtonLayout>
            </>
          }
          onClose={onClose}
        >
          <S.Body key={templateKey}>
            {loadError ? (
              <S.ImportErrorLayout>
                <S.ImportErrorTitle>
                  {t("import.load-error-title")}
                </S.ImportErrorTitle>
                <S.ImportErrorMessage>
                  {t("import.load-error-message")}
                </S.ImportErrorMessage>
                <Button iconName="refresh" onClick={handleRetryLoad}>
                  {t("import.retry")}
                </Button>
              </S.ImportErrorLayout>
            ) : !isLoading ? (
              <>
                <S.CloseHideWrapper>
                  <S.CloseHide />
                </S.CloseHideWrapper>
                <OneSchemaImporter
                  /* managing state from your application */
                  isOpen={isOpen}
                  // isOpen={true}
                  onRequestClose={() => setIsOpen(false)}
                  /* required config values */
                  clientId={process.env.REACT_APP_ONESCHEMA_CLIENT_ID ?? ""}
                  userJwt={jwtToken}
                  templateKey={templateKey}
                  /* optional config values */
                  importConfig={{
                    type: "file-upload",
                    url: uploadUrl,
                    format: "json",
                  }}
                  devMode={process.env.REACT_APP_ENV !== "production"}
                  className="oneschema-importer"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  inline={true}
                  onSuccess={(data) => {
                    props.onClose?.();
                  }}
                  onError={(message) => console.error(message)}
                  languageCode={config.userPreferences.language.value}
                />
              </>
            ) : (
              <Preloader />
            )}
          </S.Body>
        </Modal>
      </S.LocalCssVariablesWrapper>
    </ModalOverlay>
  );
});
